import React, { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Btn } from '../../../AbstractElements';
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { useIteamResource } from '../../../hook/ShowTable/generic';

/** 🔁 Nuova shape dei filtri */
export interface FormFilter {
  consumer_id: string;
  nominativo: string;
  telefono: string;
  eta: number | '';
  citta: string;

  new_cluster: string;
  monetary_regional: string;
  generational_cluster: string;
  behavioural_cluster: string;
  primo_acquisto: string; 

  segm1_key: string;
  recency: string;
  segm1_frequency: string;
  preferred_rdv: string;
  ultimo_acquisto: string; 
  nota: string; 
}

interface FiltersProps {
  filters: FormFilter;
  onFiltersChange: (filters: FormFilter, immediate?: boolean) => void;
  onOrderChange: (value: string) => void;
  orderValue?: string;
  isFiltering?: boolean;
  isLoading?: boolean;
  onExport?: (format: 'csv' | 'pdf') => void;
  exporting?: 'csv' | 'pdf' | null;
}

export interface Cluster {
  ID: number;
  campo: string | number | null;
  groupName: string;
}

type ApiResponse = {
  message: {
    status: string;
    list: Cluster[];
  };
};

export default function Filters({
  filters,
  onFiltersChange,
  onOrderChange,
  isFiltering = false,
  isLoading = false,
  orderValue,
  onExport,
  exporting = null,
}: FiltersProps) {
  const [showFilter, setShowFilter] = useState(false);
  const [formFilter, setFormFilter] = useState<FormFilter>(filters);

  const { data: payload } = useIteamResource<ApiResponse>('/cluster/list');
  const rows: Cluster[] = useMemo(
    () => (Array.isArray(payload?.message?.list) ? payload!.message.list : []),
    [payload]
  );

  const [openDrop, setOpenDrop] = useState(false);
  const toggle = () => setOpenDrop(!openDrop);

  // Mantieni in sync il form locale con i filtri del padre
  useEffect(() => {
    setFormFilter(filters);
  }, [filters]);

  const handleClick = () => setShowFilter((prev) => !prev);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const next: FormFilter = {
      ...formFilter,
      [name]:
        name === 'eta'
          ? (value === '' ? '' : Number(value)) // gestisci number | ''
          : value,
    } as FormFilter;

    setFormFilter(next);

    // debounce
    clearTimeout((window as any).filterTimeout);
    (window as any).filterTimeout = setTimeout(() => {
      onFiltersChange(next);
    }, 500);
  };

  const handleResetFilters = () => {
    const reset: FormFilter = {
      consumer_id: '',
      nominativo: '',
      telefono: '',
      eta: '',
      citta: '',

      new_cluster: '',
      monetary_regional: '',
      generational_cluster: '',
      behavioural_cluster: '',
      primo_acquisto: '',

      segm1_key: '',
      recency: '',
      segm1_frequency: '',
      preferred_rdv: '',
      ultimo_acquisto: '',
      nota: '',
    };
    clearTimeout((window as any).filterTimeout);
    setFormFilter(reset);
    onFiltersChange(reset, true);
  };

  const inputProps = { disabled: isLoading };

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== '' && v !== null && v !== undefined
  );

  return (
    <div>
      <div className="d-flex flex-column-reverse flex-md-row justify-content-between align-items-start ">
        <Btn
          className="px-4 my-3"
          color={hasActiveFilters ? 'success' : 'primary'}
          type="button"
          onClick={handleClick}
          disabled={isLoading}
        >
          {isFiltering ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Applicando filtri...
            </>
          ) : (
            <>Ricerca Avanzata {hasActiveFilters && '(Filtri Attivi)'}</>
          )}
        </Btn>

        <div className="d-flex flex-row gap-2 align-items-end mb-3">
          <FormGroup>
            <Label htmlFor="orderGroup">Ordina per</Label>
            <Input
              id="orderGroup"
              type="select"
              value={orderValue ?? ''}
              onChange={(e) => onOrderChange(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Scegli...</option>
              {rows.map((item) => (
                <option key={item.ID} value={item.ID}>
                  {item.groupName}
                </option>
              ))}
            </Input>
          </FormGroup>

          <div className="email-body mb-3">
            <Dropdown isOpen={openDrop} toggle={toggle}>
              <DropdownToggle color="transparent" dir="left" className="light-square">
                <i style={{ color: '#000' }} className="fa-solid fa-download" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-block" end>
                <DropdownItem
                  onClick={() => onExport?.('csv')}
                  disabled={!onExport || exporting !== null}
                >
                  {exporting === 'csv' ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2" />
                      Esportazione CSV…
                    </>
                  ) : (
                    'Esporta CSV'
                  )}
                </DropdownItem>
                <DropdownItem
                  onClick={() => onExport?.('pdf')}
                  disabled={!onExport || exporting !== null}
                >
                  {exporting === 'pdf' ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2" />
                      Esportazione PDF…
                    </>
                  ) : (
                    'Esporta PDF'
                  )}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      {showFilter && (
        <Card style={{ backgroundColor: '#fcfcfcff' }} className="shadow-sm mt-3">
          <CardBody>
            <Form>
              <Row>
                {/* Colonna 1 */}
                <Col md={4}>
                  <Label htmlFor="consumer_id">Consumer ID</Label>
                  <Input
                    id="consumer_id"
                    type="text"
                    placeholder="Inserisci il Consumer ID"
                    name="consumer_id"
                    value={formFilter.consumer_id}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="nominativo" className="mt-2">Nominativo</Label>
                  <Input
                    id="nominativo"
                    type="text"
                    placeholder="Inserisci nominativo"
                    name="nominativo"
                    value={formFilter.nominativo}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="telefono" className="mt-2">Telefono</Label>
                  <Input
                    id="telefono"
                    type="text"
                    placeholder="Inserisci il telefono"
                    name="telefono"
                    value={formFilter.telefono}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="eta" className="mt-2">Età</Label>
                  <Input
                    id="eta"
                    type="number"
                    placeholder="Inserisci età"
                    name="eta"
                    value={formFilter.eta}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="citta" className="mt-2">Città</Label>
                  <Input
                    id="citta"
                    type="text"
                    placeholder="Inserisci città"
                    name="citta"
                    value={formFilter.citta}
                    onChange={handleChange}
                    {...inputProps}
                  />
                </Col>

                {/* Colonna 2 */}
                <Col md={4}>
                  <Label htmlFor="new_cluster">New Cluster</Label>
                  <Input
                    id="new_cluster"
                    type="text"
                    placeholder="Inserisci New Cluster"
                    name="new_cluster"
                    value={formFilter.new_cluster}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="monetary_regional" className="mt-2">Monetary Regional</Label>
                  <Input
                    id="monetary_regional"
                    type="text"
                    placeholder="Inserisci Monetary Regional"
                    name="monetary_regional"
                    value={formFilter.monetary_regional}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="generational_cluster" className="mt-2">Generational Cluster</Label>
                  <Input
                    id="generational_cluster"
                    type="text"
                    placeholder="Inserisci generational cluster"
                    name="generational_cluster"
                    value={formFilter.generational_cluster}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="behavioural_cluster" className="mt-2">Behavioural Cluster</Label>
                  <Input
                    id="behavioural_cluster"
                    type="text"
                    placeholder="Inserisci behavioural cluster"
                    name="behavioural_cluster"
                    value={formFilter.behavioural_cluster}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="primo_acquisto" className="mt-2">Primo Acquisto</Label>
                  <Input
                    id="primo_acquisto"
                    type="text"
                    placeholder="Inserisci data primo acquisto"
                    name="primo_acquisto"
                    value={formFilter.primo_acquisto}
                    onChange={handleChange}
                    {...inputProps}
                  />
                </Col>

                {/* Colonna 3 */}
                <Col md={4}>
                  <Label htmlFor="segm1_key">Segm1 Key</Label>
                  <Input
                    id="segm1_key"
                    type="text"
                    placeholder="Inserisci segm1 key"
                    name="segm1_key"
                    value={formFilter.segm1_key}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="recency" className="mt-2">Recency</Label>
                  <Input
                    id="recency"
                    type="text"
                    placeholder="Inserisci Recency"
                    name="recency"
                    value={formFilter.recency}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="segm1_frequency" className="mt-2">Segm1 Frequency</Label>
                  <Input
                    id="segm1_frequency"
                    type="text"
                    placeholder="Inserisci segm1 frequency"
                    name="segm1_frequency"
                    value={formFilter.segm1_frequency}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="preferred_rdv" className="mt-2">Preferred RDV</Label>
                  <Input
                    id="preferred_rdv"
                    type="text"
                    placeholder="Inserisci preferred rdv"
                    name="preferred_rdv"
                    value={formFilter.preferred_rdv}
                    onChange={handleChange}
                    {...inputProps}
                  />

                  <Label htmlFor="ultimo_acquisto" className="mt-2">Ultimo Acquisto</Label>
                  <Input
                    id="ultimo_acquisto"
                    type="text"
                    placeholder="Inserisci data Ultimo Acquisto"
                    name="ultimo_acquisto"
                    value={formFilter.ultimo_acquisto}
                    onChange={handleChange}
                    {...inputProps}
                  />
                </Col>
              </Row>

              <Row className="mt-3">
                <Col className="d-flex gap-2">
                  <Btn color="secondary" type="button" onClick={handleResetFilters}>
                    Reset Filtri
                  </Btn>
                  <small className="text-muted align-self-center">
                    {isFiltering ? (
                      <span className="text-primary">
                        <i className="fa fa-spinner fa-spin me-1"></i>Applicando filtri...
                      </span>
                    ) : (
                      ''
                    )}
                  </small>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
